import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { TeamWeekAside, HelpAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader, SavedNote, Section, Select, SidePanel, Switch } from '../components/forms';
import { CalendarFeed } from '../components/CalendarFeed';
import { SortHandle, useSortable } from '../components/Sortable';
import { Button, ErrorNote, Field } from '../components/ui';
import { WeekHoursEditor, type WeekHours } from '../components/WeekHoursEditor';
import { api, ApiError, type Salon } from '../lib/api';
import { useLocale, useT, type TKey } from '../lib/i18n';
import { useLocations } from '../lib/location';
import { addDays, formatTime, instantAt, minuteOfDay, SALON_TZ, todayIn } from '../lib/time';
import { useFirstLocation } from './setup-hours';
import { errorText } from './setup-services';

type Member = { id: string; displayName: string; color: string; bookableOnline: boolean; hoursMode: 'opening_hours' | 'custom'; role: 'owner' | 'manager' | 'staff' | null; invited: boolean };
type Access = { state: 'member'; role: 'owner' | 'manager' | 'staff'; email: string } | { state: 'invited'; role: 'manager' | 'staff'; email: string | null; expiresAt: string } | { state: 'none' };
type MemberDetail = Omit<Member, 'role'> & { services: 'all' | string[]; hours: WeekHours };
type Category = { id: string; name: string; services: Array<{ id: string; name: string }> };
type Block = { id: string; startsAt: string; endsAt: string; allDay: boolean; reason: string; note: string | null; recurrenceRule: string | null; recurrenceUntil: string | null; staffMemberId: string | null };

const colors = ['#2F6B5B', '#7C5CBF', '#C2562F', '#2F6FA8', '#B0457A', '#8A7A2E', '#3F8F8B', '#6B6B6B'];
const roleKey: Record<string, TKey> = { owner: 'team.owner', manager: 'team.manager', staff: 'team.staff' };

export function TeamPage() {
  const t = useT();
  const { orgId } = useParams({ from: '/s/$orgId/salon/team' });
  const team = useQuery({ queryKey: ['team', orgId], queryFn: () => api<{ team: Member[] }>(`/orgs/${orgId}/team`) });
  const [open, setOpen] = useState<{ id?: string } | null>(null);
  const qc = useQueryClient();
  const members = team.data?.team ?? [];
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api(`/orgs/${orgId}/team/order`, { method: 'PUT', body: JSON.stringify({ ids }) }),
    onMutate: (ids) => qc.setQueryData<{ team: Member[] }>(['team', orgId], (d) => (d ? { team: ids.map((id) => d.team.find((m) => m.id === id)!).filter(Boolean) } : d)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['team', orgId] });
      void qc.invalidateQueries({ queryKey: ['calendar', orgId] });
    },
  });
  const sort = useSortable(members.map((m) => m.id), (ids) => reorder.mutate(ids));

  return (
    <SettingsPage orgId={orgId} aside={<><TeamWeekAside orgId={orgId} /><HelpAside slugs={['team']} /></>}>
      <PageHeader orgId={orgId} title={t('team.title')} intro={t('team.intro')} action={<Button onClick={() => setOpen({})}>+ {t('team.add')}</Button>} />
      <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
        {members.map((m) => (
          <li key={m.id} {...sort.itemProps(m.id)} className="flex items-center bg-surface pl-1.5">
            {members.length > 1 && <SortHandle label={m.displayName} onPointerDown={(e) => sort.start(e, m.id)} onKeyDown={(e) => sort.onKey(e, m.id)} />}
            <button className="flex flex-1 items-center gap-3 py-3 pl-2 pr-4 text-left hover:bg-subtle" onClick={() => setOpen({ id: m.id })}>
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full font-semibold text-white" style={{ background: m.color }}>
                {m.displayName.slice(0, 1).toUpperCase()}
              </span>
              <span className="flex-1">
                <span className="font-medium">{m.displayName}</span>
                <span className="block text-sm text-muted">
                  {m.hoursMode === 'custom' ? t('team.ownHours') : t('team.followsHours')}
                  {!m.bookableOnline && ` · ${t('services.notOnline')}`}
                </span>
              </span>
              <span className="text-sm text-muted">{m.role ? t(roleKey[m.role]!) : m.invited ? t('access.invitedShort') : t('team.noLogin')}</span>
            </button>
          </li>
        ))}
      </ul>
      {/* Only matters once there are colleagues. */}
      {members.length > 1 && <StaffSwitches orgId={orgId} />}
      {open && <MemberPanel orgId={orgId} memberId={open.id} isOwner={team.data?.team.find((m) => m.id === open.id)?.role === 'owner'} onClose={() => setOpen(null)} />}
    </SettingsPage>
  );
}

type Switches = { staffSeeAllAgendas: boolean; staffSeeClientContact: boolean; staffEditOthers: boolean };

/** The three switches for the Medewerker role (plan 3.2); each change saves straight away. */
function StaffSwitches({ orgId }: { orgId: string }) {
  const t = useT();
  const qc = useQueryClient();
  const key = ['staff-switches', orgId];
  const switches = useQuery({ queryKey: key, queryFn: () => api<Switches>(`/orgs/${orgId}/staff-switches`) });
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (next: Switches) => api<Switches>(`/orgs/${orgId}/staff-switches`, { method: 'PUT', body: JSON.stringify(next) }),
    onMutate: (next) => qc.setQueryData(key, next),
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const s = switches.data;
  if (!s) return null;
  const set = (patch: Partial<Switches>) => save.mutate({ ...s, ...patch });
  return (
    <Section title={t('switches.title')} intro={t('switches.intro')} action={<SavedNote show={saved} />}>
      <Switch label={t('switches.allAgendas')} hint={t('switches.allAgendasHint')} checked={s.staffSeeAllAgendas} onChange={(v) => set({ staffSeeAllAgendas: v })} />
      <Switch label={t('switches.contact')} hint={t('switches.contactHint')} checked={s.staffSeeClientContact} onChange={(v) => set({ staffSeeClientContact: v })} />
      <Switch label={t('switches.editOthers')} hint={t('switches.editOthersHint')} checked={s.staffEditOthers} onChange={(v) => set({ staffEditOthers: v })} />
      <ErrorNote message={save.error instanceof ApiError ? save.error.message : save.error ? t('error.generic') : null} />
    </Section>
  );
}

function MemberPanel({ orgId, memberId, isOwner, onClose }: { orgId: string; memberId?: string; isOwner: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const location = useFirstLocation(orgId);
  const detail = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => api<{ member: MemberDetail }>(`/orgs/${orgId}/team/${memberId}`),
    enabled: !!memberId,
  });
  const catalog = useQuery({ queryKey: ['catalog', orgId], queryFn: () => api<{ categories: Category[] }>(`/orgs/${orgId}/catalog`) });
  if (memberId && !detail.data) return <SidePanel open title={t('common.loading')} onClose={onClose}>{null}</SidePanel>;
  return (
    <MemberForm
      key={memberId ?? 'new'}
      orgId={orgId}
      locationId={location.data?.id}
      initial={detail.data?.member}
      categories={catalog.data?.categories ?? []}
      isOwner={isOwner}
      onClose={onClose}
      onSaved={() => {
        qc.invalidateQueries({ queryKey: ['team', orgId] });
        qc.invalidateQueries({ queryKey: ['catalog', orgId] });
        if (memberId) qc.invalidateQueries({ queryKey: ['member', memberId] });
      }}
    />
  );
}

const defaultWeek: WeekHours = { '2': [{ start: '09:00', end: '17:30' }], '3': [{ start: '09:00', end: '17:30' }], '4': [{ start: '09:00', end: '17:30' }], '5': [{ start: '09:00', end: '17:30' }], '6': [{ start: '09:00', end: '17:00' }] };

function MemberForm({ orgId, locationId, initial, categories, isOwner, onClose, onSaved }: {
  orgId: string;
  locationId?: string;
  initial?: MemberDetail;
  categories: Category[];
  isOwner: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(initial?.displayName ?? '');
  const [color, setColor] = useState(initial?.color);
  const [online, setOnline] = useState(initial?.bookableOnline ?? true);
  const [mode, setMode] = useState<'opening_hours' | 'custom'>(initial?.hoursMode ?? 'opening_hours');
  const [hours, setHours] = useState<WeekHours>(initial && Object.keys(initial.hours).length ? initial.hours : defaultWeek);
  const allIds = categories.flatMap((c) => c.services.map((s) => s.id));
  const [services, setServices] = useState<'all' | string[]>(initial?.services ?? 'all');
  const chosen = new Set(services === 'all' ? allIds : services);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { displayName: name.trim(), color, bookableOnline: online, hoursMode: mode, services };
      const id = initial
        ? (await api(`/orgs/${orgId}/team/${initial.id}`, { method: 'PUT', body: JSON.stringify(payload) }), initial.id)
        : (await api<{ id: string }>(`/orgs/${orgId}/team`, { method: 'POST', body: JSON.stringify(payload) })).id;
      if (mode === 'custom' && locationId) {
        await api(`/orgs/${orgId}/team/${id}/hours`, { method: 'PUT', body: JSON.stringify({ locationId, hours }) });
      }
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/team/${initial!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  function toggleService(id: string) {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setServices(next.size === allIds.length ? 'all' : [...next]);
  }

  return (
    <SidePanel
      open
      title={initial ? initial.displayName : t('member.new')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {initial && !isOwner && (
            <Button variant="ghost" className="ml-auto text-danger" onClick={() => window.confirm(t('member.deleteConfirm')) && remove.mutate()}>
              {t('common.delete')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field id="m-name" required maxLength={40} label={t('member.name')} hint={t('member.nameHint')} value={name} onChange={(e) => setName(e.target.value)} />

        <fieldset>
          <legend className="mb-2 text-sm font-semibold">{t('member.color')}</legend>
          <div className="flex flex-wrap gap-2">
            {colors.map((c, i) => (
              <button
                key={c}
                type="button"
                aria-label={`${t('member.color')} ${i + 1}`}
                aria-pressed={color === c}
                onClick={() => setColor(c)}
                className={`size-9 rounded-full ring-offset-2 ring-offset-canvas ${color === c ? 'ring-2 ring-ink' : ''}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </fieldset>

        <Switch label={t('member.online')} hint={t('member.onlineHint')} checked={online} onChange={setOnline} />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium">{t('member.hours')}</legend>
          {(['opening_hours', 'custom'] as const).map((m) => (
            <label key={m} className="flex items-center gap-2">
              <input type="radio" name="hours-mode" className="size-5 accent-[var(--color-accent)]" checked={mode === m} onChange={() => setMode(m)} />
              {m === 'opening_hours' ? t('member.hoursFollow') : t('member.hoursOwn')}
            </label>
          ))}
          {mode === 'custom' && <WeekHoursEditor value={hours} onChange={setHours} closedLabel={t('hours.notWorking')} />}
        </fieldset>

        {categories.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 font-medium">{t('member.services')}</legend>
            <label className="flex items-center gap-2">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={services === 'all'} onChange={(e) => setServices(e.target.checked ? 'all' : [])} />
              {t('member.allServices')}
            </label>
            {services !== 'all' &&
              categories.map((c) => (
                <div key={c.id} className="pl-6">
                  <p className="mb-1 text-sm font-semibold text-muted">{c.name}</p>
                  {c.services.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 py-0.5">
                      <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={chosen.has(s.id)} onChange={() => toggleService(s.id)} />
                      {s.name}
                    </label>
                  ))}
                </div>
              ))}
          </fieldset>
        )}

        {initial && <StaffLocations orgId={orgId} staffMemberId={initial.id} />}

        {initial && <AppAccess orgId={orgId} staffMemberId={initial.id} name={initial.displayName} onChanged={onSaved} />}

        {initial && <CalendarFeed orgId={orgId} staffMemberId={initial.id} own={false} />}

        {initial && locationId && <BlockedTime orgId={orgId} locationId={locationId} staffMemberId={initial.id} />}

        {!initial && <AgendaPriceNote orgId={orgId} />}

        <ErrorNote message={errorText(save.error ?? remove.error, t('error.generic'))} />
      </div>
    </SidePanel>
  );
}

/** Where someone works (only with 2+ locations). Everywhere by default. */
function StaffLocations({ orgId, staffMemberId }: { orgId: string; staffMemberId: string }) {
  const t = useT();
  const qc = useQueryClient();
  const locations = useLocations(orgId);
  const key = ['staff-locations', orgId, staffMemberId];
  const current = useQuery({ queryKey: key, queryFn: () => api<{ locations: 'all' | string[] }>(`/orgs/${orgId}/team/${staffMemberId}/locations`).then((r) => r.locations) });
  const save = useMutation({
    mutationFn: (sel: 'all' | string[]) => api<{ locations: 'all' | string[] }>(`/orgs/${orgId}/team/${staffMemberId}/locations`, { method: 'PUT', body: JSON.stringify({ locations: sel }) }).then((r) => r.locations),
    onSuccess: (sel) => {
      qc.setQueryData(key, sel);
      void qc.invalidateQueries({ queryKey: ['calendar', orgId] });
    },
  });
  if (!locations.data || locations.data.length < 2 || !current.data) return null;
  const all = locations.data.map((l) => l.id);
  const chosen = new Set(current.data === 'all' ? all : current.data);
  const toggle = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (next.size === 0) return; // at least one
    save.mutate(next.size === all.length ? 'all' : [...next]);
  };
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 font-medium">{t('loc.where')}</legend>
      {locations.data.map((l) => (
        <label key={l.id} className="flex items-center gap-2">
          <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={chosen.has(l.id)} onChange={() => toggle(l.id)} disabled={save.isPending} />
          {l.name}
        </label>
      ))}
      <ErrorNote message={errorText(save.error, t('error.generic'))} />
    </fieldset>
  );
}

/**
 * App-toegang: a team member is a calendar column; giving them a login is optional. One email
 * invitation, and their account is linked to their own column.
 */
function AppAccess({ orgId, staffMemberId, name, onChanged }: { orgId: string; staffMemberId: string; name: string; onChanged: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const key = ['access', orgId, staffMemberId];
  const access = useQuery({ queryKey: key, queryFn: () => api<{ access: Access }>(`/orgs/${orgId}/team/${staffMemberId}/access`).then((r) => r.access) });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'staff' | 'manager'>('staff');
  const [editing, setEditing] = useState(false);
  const done = (a: Access | undefined) => {
    if (a) qc.setQueryData(key, a);
    else void qc.invalidateQueries({ queryKey: key });
    setEditing(false);
    onChanged();
  };
  const invite = useMutation({
    mutationFn: (body: { email: string; role: 'staff' | 'manager' }) => api<{ access: Access }>(`/orgs/${orgId}/team/${staffMemberId}/invite`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.access),
    onSuccess: done,
  });
  const changeRole = useMutation({
    mutationFn: (r: 'staff' | 'manager') => api<{ access: Access }>(`/orgs/${orgId}/team/${staffMemberId}/role`, { method: 'PUT', body: JSON.stringify({ role: r }) }).then((x) => x.access),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api(`/orgs/${orgId}/team/${staffMemberId}/access`, { method: 'DELETE' }), onSuccess: () => done(undefined) });
  const isOwner = salon.data?.role === 'owner';
  const a = access.data;
  if (!a) return null;
  const error = errorText(invite.error ?? changeRole.error ?? remove.error, t('error.generic'));

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-5">
      <div>
        <h3 className="font-semibold">{t('access.title')}</h3>
        <p className="text-sm text-muted">{t('access.intro')}</p>
      </div>
      {a.state === 'member' && (
        <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-subtle px-3 py-2.5">
          <p>
            <span className="font-medium">{a.email}</span>
            <span className="text-muted"> · {t(roleKey[a.role]!)}</span>
          </p>
          {a.role !== 'owner' && (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {isOwner && (
                <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={() => changeRole.mutate(a.role === 'manager' ? 'staff' : 'manager')}>
                  {a.role === 'manager' ? t('access.makeStaff') : t('access.makeManager')}
                </button>
              )}
              {(isOwner || a.role === 'staff') && (
                <button type="button" className="text-sm font-semibold text-danger hover:underline" onClick={() => window.confirm(t('access.removeConfirm', { name })) && remove.mutate()}>
                  {t('access.remove')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {a.state === 'invited' && !editing && (
        <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-subtle px-3 py-2.5">
          <p>
            {t('access.invited', { email: a.email ?? '' })}
            <span className="text-muted"> · {t(roleKey[a.role]!)}</span>
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={() => a.email && invite.mutate({ email: a.email, role: a.role })} disabled={invite.isPending}>
              {invite.isSuccess ? `✓ ${t('access.resent')}` : t('access.resend')}
            </button>
            <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={() => { setEmail(a.email ?? ''); setRole(a.role); setEditing(true); }}>
              {t('access.otherEmail')}
            </button>
            <button type="button" className="text-sm font-semibold text-danger hover:underline" onClick={() => remove.mutate()}>
              {t('access.withdraw')}
            </button>
          </div>
        </div>
      )}
      {(a.state === 'none' || editing) && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate({ email, role });
          }}
        >
          <Field id="access-email" type="email" required label={t('access.email')} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          {isOwner && (
            <Select id="access-role" label={t('access.role')} hint={role === 'manager' ? t('access.managerHint') : t('access.staffHint')} value={role} onChange={(e) => setRole(e.target.value as 'staff' | 'manager')}>
              <option value="staff">{t('team.staff')}</option>
              <option value="manager">{t('team.manager')}</option>
            </Select>
          )}
          <Button type="submit" variant="secondary" className="self-start" disabled={invite.isPending}>
            {t('access.send')}
          </Button>
        </form>
      )}
      <ErrorNote message={error} />
    </section>
  );
}

const reasons = ['pause', 'day_off', 'sick', 'vacation', 'meeting', 'other'] as const;

/** Blocked time for one team member: list of upcoming blocks, add one, report sick for today. */
function BlockedTime({ orgId, locationId, staffMemberId }: { orgId: string; locationId: string; staffMemberId: string }) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const now = new Date();
  const horizon = new Date(now.getTime() + 180 * 86_400_000);
  const key = ['blocks', staffMemberId];
  const blocks = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ blocks: Block[] }>(`/orgs/${orgId}/blocks?${new URLSearchParams({ from: now.toISOString(), to: horizon.toISOString(), staffMemberId })}`),
    select: (d) => d.blocks.filter((b) => b.staffMemberId === staffMemberId),
  });
  const add = useMutation({
    mutationFn: (b: unknown) => api(`/orgs/${orgId}/blocks`, { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['calendar', orgId] });
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/orgs/${orgId}/blocks/${id}`, { method: 'DELETE' }), onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['calendar', orgId] });
    },
  });

  const [date, setDate] = useState(() => todayIn(SALON_TZ));
  const [allDay, setAllDay] = useState(false);
  const [from, setFrom] = useState('12:30');
  const [until, setUntil] = useState('13:00');
  const [reason, setReason] = useState<(typeof reasons)[number]>('pause');
  const [repeat, setRepeat] = useState<'none' | 'daily' | 'weekly'>('none');
  const [repeatUntil, setRepeatUntil] = useState('');

  /** Wall-clock time at the salon → ISO, whatever time zone the device is in. */
  const iso = (d: string, time: string) => {
    const [h, m] = time.split(':').map(Number) as [number, number];
    return instantAt(d, h * 60 + m, SALON_TZ);
  };
  function submit() {
    add.mutate({
      locationId,
      staffMemberId,
      startsAt: iso(date, allDay ? '00:00' : from),
      endsAt: allDay ? instantAt(addDays(date, 1), 0, SALON_TZ) : iso(date, until),
      allDay,
      reason,
      repeat,
      repeatUntil: repeat === 'none' ? undefined : repeatUntil,
    });
  }
  function sickToday() {
    // The rest of today: from now (on the 5-minute grid) until midnight at the salon.
    const today = todayIn(SALON_TZ);
    const startsAt = instantAt(today, Math.floor(minuteOfDay(Date.now(), SALON_TZ) / 5) * 5, SALON_TZ);
    add.mutate({ locationId, staffMemberId, startsAt, endsAt: instantAt(addDays(today, 1), 0, SALON_TZ), reason: 'sick' });
  }

  const dt = new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { timeZone: SALON_TZ, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const d = new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { timeZone: SALON_TZ, weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-5">
      <div>
        <h3 className="font-semibold">{t('member.blocked')}</h3>
        <p className="text-sm text-muted">{t('member.blockedIntro')}</p>
      </div>
      <Button variant="secondary" className="self-start" onClick={sickToday} disabled={add.isPending}>
        {t('member.sickToday')}
      </Button>
      {blocks.data?.length === 0 && <p className="text-sm text-muted">{t('member.noBlocks')}</p>}
      <ul className="flex flex-col gap-2">
        {blocks.data?.map((b) => (
          <li key={b.id} className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] bg-subtle px-3 py-2">
            <span className="text-sm">
              <span className="font-medium">{t(`reason.${b.reason}` as TKey)}</span>
              {' · '}
              {b.allDay ? d.format(new Date(b.startsAt)) : `${dt.format(new Date(b.startsAt))}–${formatTime(b.endsAt, SALON_TZ)}`}
              {b.recurrenceRule && ` · ${b.recurrenceRule.includes('DAILY') ? t('block.repeatDaily') : t('block.repeatWeekly')}`}
            </span>
            <button className="rounded-[var(--radius-control)] px-2 py-1 text-sm text-muted hover:bg-surface" onClick={() => remove.mutate(b.id)}>
              {t('common.delete')}
            </button>
          </li>
        ))}
      </ul>
      <div className="grid gap-3 rounded-[var(--radius-control)] border border-line p-3 sm:grid-cols-2">
        <Field id="blk-date" type="date" label={t('block.date')} value={date} onChange={(e) => setDate(e.target.value)} />
        <Select id="blk-reason" label={t('block.reason')} value={reason} onChange={(e) => setReason(e.target.value as (typeof reasons)[number])}>
          {reasons.map((r) => (
            <option key={r} value={r}>
              {t(`reason.${r}`)}
            </option>
          ))}
        </Select>
        <div className="sm:col-span-2">
          <Switch label={t('block.allDay')} checked={allDay} onChange={setAllDay} />
        </div>
        {!allDay && (
          <>
            <Field id="blk-from" type="time" step={300} label={t('block.from')} value={from} onChange={(e) => setFrom(e.target.value)} />
            <Field id="blk-until" type="time" step={300} label={t('block.until')} value={until} onChange={(e) => setUntil(e.target.value)} />
          </>
        )}
        <Select id="blk-repeat" label={t('block.repeat')} value={repeat} onChange={(e) => setRepeat(e.target.value as typeof repeat)}>
          <option value="none">{t('block.repeatNone')}</option>
          <option value="daily">{t('block.repeatDaily')}</option>
          <option value="weekly">{t('block.repeatWeekly')}</option>
        </Select>
        {repeat !== 'none' && <Field id="blk-repeat-until" type="date" label={t('block.repeatUntil')} value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} />}
        <div className="sm:col-span-2">
          <ErrorNote message={errorText(add.error, t('error.generic'))} />
        </div>
        <Button variant="secondary" className="self-start" onClick={submit} disabled={add.isPending}>
          {t('block.add')}
        </Button>
      </div>
    </section>
  );
}

type BillingPrices = { comped: boolean; subscription: { status: string; interval: 'month' | 'year' } | null; prices: Record<'month' | 'year', { excl: number }>; withOneMore: Record<'month' | 'year', { excl: number }> };

/** Before adding someone: what it does to a running subscription (agendas follow the team). */
function AgendaPriceNote({ orgId }: { orgId: string }) {
  const t = useT();
  const billing = useQuery({ queryKey: ['billing', orgId], queryFn: () => api<BillingPrices>(`/orgs/${orgId}/billing`) });
  const b = billing.data;
  if (!b || b.comped || b.subscription?.status !== 'active') return null;
  const k = b.subscription.interval;
  const euro = (c: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(c / 100);
  return (
    <p className="rounded-[var(--radius-control)] bg-accent-soft px-4 py-3 text-sm text-accent-strong">
      {t('team.priceNote', { from: euro(b.prices[k].excl), to: euro(b.withOneMore[k].excl), per: t(k === 'month' ? 'sub.perMonth' : 'sub.perYear') })}
    </p>
  );
}
